import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { KpiCard } from '../components/common/KpiCard';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { PaymentDialog } from '../components/common/PaymentDialog';
import { fmtEur } from '../lib/constants';
import { printReceipt } from '../lib/receipt';
import { useRealtime } from '../lib/realtime';
import { Euro, Banknote, CreditCard, Wallet, Receipt, Printer } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PaymentsPage() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const load = async () => {
    const [p, o] = await Promise.all([api.get('/payments'), api.get('/orders', { params: { active: true } })]);
    setPayments(p.data); setOrders(o.data);
  };
  useEffect(() => { load(); const tm = setInterval(load, 12000); return () => clearInterval(tm); }, []);

  useRealtime((msg) => {
    if (msg && (msg.event === 'payments' || msg.event === 'orders' || msg.event === 'tables')) load();
  });

  const today = payments.filter((p) => (p.created_at || '').slice(0, 10) === todayStr());
  const sum = (arr, f) => arr.reduce((s, x) => s + Number(f(x) || 0), 0);
  const totalToday = sum(today, (p) => p.amount);
  const cashToday = sum(today, (p) => p.cash);
  const cardToday = sum(today, (p) => p.card);

  const openPay = async (o) => {
    const { data } = await api.get(`/orders/${o.id}`);
    setPayOrder(data); setPayOpen(true);
  };

  const printSummary = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/receipt`, { params: { type: 'resoconto' } });
      if (data.print_agent_enabled) {
        await api.post('/print/enqueue', { order_id: o.id, type: 'preconto' });
        toast.success(t('payments.summarySentToPrinter'));
      } else {
        printReceipt(data);
        toast.success(t('payments.summaryPrinting'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('payments.summaryError'));
    }
  };

  const reprintReceipt = async (p) => {
    try {
      const { data } = await api.get(`/orders/${p.order_id}/receipt`, { params: { type: 'scontrino' } });
      if (data.print_agent_enabled) {
        await api.post('/print/enqueue', { order_id: p.order_id, type: 'receipt' });
        toast.success(t('payments.receiptSentToPrinter', { table: p.table_name }));
      } else {
        printReceipt(data);
        toast.success(t('payments.receiptReprinting', { table: p.table_name }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('payments.receiptError'));
    }
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader title={t('payments.title')} subtitle={t('payments.subtitle')} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <KpiCard label={t('payments.todayRevenue')} value={fmtEur(totalToday)} icon={Euro} accent="free" />
        <KpiCard label={t('payments.todayCash')} value={fmtEur(cashToday)} icon={Banknote} accent="primary" />
        <KpiCard label={t('payments.todayCard')} value={fmtEur(cardToday)} icon={CreditCard} accent="accent" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        <Card className="p-5">
          <div className="font-semibold mb-3 flex items-center gap-2"><Wallet className="h-4 w-4" /> {t('payments.toCollect')}</div>
          {orders.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t('payments.noOrdersToCollect')}</div> : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div><div className="font-medium">{o.table_name}</div><div className="text-xs text-muted-foreground">{(o.items || []).length} {t('payments.items')} · {fmtEur(o.total)}</div></div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => printSummary(o)} className="rounded-lg" data-testid="print-summary-button" title={t('payments.printSummaryTitle')}><Receipt className="h-4 w-4" /></Button>
                    <Button size="sm" onClick={() => openPay(o)} className="rounded-lg" data-testid="pay-order-button">{t('payments.collect')}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="font-semibold mb-3">{t('payments.paymentHistory')}</div>
          <div className="text-xs text-muted-foreground mb-3">{t('payments.reprintHint')}</div>
          <ScrollArea className="max-h-[480px]">
            <Table data-testid="payments-history-table">
              <TableHeader>
                <TableRow><TableHead>{t('payments.time')}</TableHead><TableHead>{t('payments.table')}</TableHead><TableHead>{t('payments.method')}</TableHead><TableHead className="text-right">{t('payments.amount')}</TableHead><TableHead className="text-right">{t('payments.actions')}</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => {
                  const isToday = (p.created_at || '').slice(0, 10) === todayStr();
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm text-muted-foreground">{new Date(p.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>{p.table_name}</TableCell>
                      <TableCell>{p.method === 'cash' ? t('payments.cash') : p.method === 'card' ? t('payments.card') : t('payments.mixed')}</TableCell>
                      <TableCell className="text-right font-medium tabular">{fmtEur(p.amount)}</TableCell>
                      <TableCell className="text-right">
                        {isToday ? (
                          <Button size="sm" variant="outline" onClick={() => reprintReceipt(p)} className="rounded-lg" data-testid="reprint-receipt-button" title={t('payments.reprintReceiptTitle')}>
                            <Printer className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('payments.noPayments')}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      </div>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={payOrder} onPaid={() => { setPayOpen(false); load(); }} />
    </div>
  );
}
